import React, { Suspense, lazy, useLayoutEffect, useState } from 'react';
import { Routes, Route, Navigate, Link, useLocation, useSearchParams } from 'react-router-dom';
import { MailWarning, X } from 'lucide-react';
import { MotionConfig } from 'framer-motion';
import Layout from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';
import { PageLoader } from './components/Spinner';
import { useAnalyticsPageviews } from './lib/analytics';
import { useAuth } from './context/AuthContext';

const HomePage = lazy(() => import('./pages/HomePage'));
const SearchPage = lazy(() => import('./pages/SearchPage'));
const CodeDetailPage = lazy(() => import('./pages/CodeDetailPage'));
const ChaptersPage = lazy(() => import('./pages/ChaptersPage'));
const ChapterPage = lazy(() => import('./pages/ChapterPage'));
const BlockPage = lazy(() => import('./pages/BlockPage'));
const IndexBrowserPage = lazy(() => import('./pages/IndexBrowserPage'));
const GemsPage = lazy(() => import('./pages/GemsPage'));
const UpdatesPage = lazy(() => import('./pages/UpdatesPage'));
const FavoritesPage = lazy(() => import('./pages/FavoritesPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const NoteToCodesPage = lazy(() => import('./pages/NoteToCodesPage'));
const QueryBuilderPage = lazy(() => import('./pages/QueryBuilderPage'));
const MedicalNecessityPage = lazy(() => import('./pages/MedicalNecessityPage'));
const GuidelinesChatPage = lazy(() => import('./pages/GuidelinesChatPage'));
const CodeSetsPage = lazy(() => import('./pages/CodeSetsPage'));
const CodeSetDetailPage = lazy(() => import('./pages/CodeSetDetailPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const MicrosoftCallbackPage = lazy(() => import('./pages/MicrosoftCallbackPage'));
const AccountPage = lazy(() => import('./pages/AccountPage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const TeamPage = lazy(() => import('./pages/TeamPage'));
const AcceptInvitePage = lazy(() => import('./pages/AcceptInvitePage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

// ---------- Admin console (full-screen shell, rendered outside the public Layout) ----------
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout'));
const AdminNotFound = lazy(() => import('./pages/admin/AdminLayout').then((m) => ({ default: m.AdminNotFound })));
const OverviewPage = lazy(() => import('./pages/admin/OverviewPage'));
const AnalyticsTrafficPage = lazy(() => import('./pages/admin/AnalyticsTrafficPage'));
const AnalyticsSourcesPage = lazy(() => import('./pages/admin/AnalyticsSourcesPage'));
const AnalyticsAudiencePage = lazy(() => import('./pages/admin/AnalyticsAudiencePage'));
const RealtimePage = lazy(() => import('./pages/admin/RealtimePage'));
const FunnelPage = lazy(() => import('./pages/admin/FunnelPage'));
const RetentionPage = lazy(() => import('./pages/admin/RetentionPage'));
const ProductUsagePage = lazy(() => import('./pages/admin/ProductUsagePage'));
const RevenuePage = lazy(() => import('./pages/admin/RevenuePage'));
const CrmContactsPage = lazy(() => import('./pages/admin/CrmContactsPage'));
const CrmContactDetailPage = lazy(() => import('./pages/admin/CrmContactDetailPage'));
const CrmPipelinePage = lazy(() => import('./pages/admin/CrmPipelinePage'));
const CrmLeadsPage = lazy(() => import('./pages/admin/CrmLeadsPage'));
const CrmTasksPage = lazy(() => import('./pages/admin/CrmTasksPage'));
const CrmSegmentsPage = lazy(() => import('./pages/admin/CrmSegmentsPage'));
const AutomationsPage = lazy(() => import('./pages/admin/AutomationsPage'));
const AutomationEditorPage = lazy(() => import('./pages/admin/AutomationEditorPage'));
const AutomationRunsPage = lazy(() => import('./pages/admin/AutomationRunsPage'));
const EmailTemplatesPage = lazy(() => import('./pages/admin/EmailTemplatesPage'));
const EmailTemplateEditorPage = lazy(() => import('./pages/admin/EmailTemplateEditorPage'));
const CampaignsPage = lazy(() => import('./pages/admin/CampaignsPage'));
const EmailLogsPage = lazy(() => import('./pages/admin/EmailLogsPage'));
const WebhooksPage = lazy(() => import('./pages/admin/WebhooksPage'));
const UsersPage = lazy(() => import('./pages/admin/UsersPage'));
const TeamsPage = lazy(() => import('./pages/admin/TeamsPage'));
const DataImportsPage = lazy(() => import('./pages/admin/DataImportsPage'));
const AbbreviationsPage = lazy(() => import('./pages/admin/AbbreviationsPage'));
const SynonymsPage = lazy(() => import('./pages/admin/SynonymsPage'));
const SystemHealthPage = lazy(() => import('./pages/admin/SystemHealthPage'));
const ErrorsPage = lazy(() => import('./pages/admin/ErrorsPage'));
const AuditLogPage = lazy(() => import('./pages/admin/AuditLogPage'));
const AdminSettingsPage = lazy(() => import('./pages/admin/AdminSettingsPage'));
const BlogHomePage = lazy(() => import('./pages/admin/BlogHomePage'));
const BlogPostsPage = lazy(() => import('./pages/admin/BlogPostsPage'));
const BlogEditorPage = lazy(() => import('./pages/admin/BlogEditorPage'));
const BlogTopicsPage = lazy(() => import('./pages/admin/BlogTopicsPage'));
const BlogAutopilotPage = lazy(() => import('./pages/admin/BlogAutopilotPage'));
const BlogSettingsPage = lazy(() => import('./pages/admin/BlogSettingsPage'));
const InboxPage = lazy(() => import('./pages/admin/InboxPage'));
const NewsletterPage = lazy(() => import('./pages/admin/NewsletterPage'));
const WeeklyReportPage = lazy(() => import('./pages/admin/WeeklyReportPage'));
const AdminThemePage = lazy(() => import('./pages/admin/AdminThemePage'));
const AuthProvidersPage = lazy(() => import('./pages/admin/AuthProvidersPage'));
const AiEnginePage = lazy(() => import('./pages/admin/AiEnginePage'));
const AiPlansPage = lazy(() => import('./pages/admin/AiPlansPage'));
const AiUsagePage = lazy(() => import('./pages/admin/AiUsagePage'));
const AiLabPage = lazy(() => import('./pages/admin/AiLabPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const DashboardLayout = lazy(() => import('./pages/dashboard/DashboardLayout'));
const DashOverviewPage = lazy(() => import('./pages/dashboard/DashOverviewPage'));
const DashCodesPage = lazy(() => import('./pages/dashboard/DashCodesPage'));
const DashAiPage = lazy(() => import('./pages/dashboard/DashAiPage'));
const DashBillingPage = lazy(() => import('./pages/dashboard/DashBillingPage'));
const DashNotificationsPage = lazy(() => import('./pages/dashboard/DashNotificationsPage'));
const DashSecurityPage = lazy(() => import('./pages/dashboard/DashSecurityPage'));
const DashPrivacyPage = lazy(() => import('./pages/dashboard/DashPrivacyPage'));
const DashSupportPage = lazy(() => import('./pages/dashboard/DashSupportPage'));
const DashProfilePage = lazy(() => import('./pages/dashboard/DashProfilePage'));
const DashIssuesPage = lazy(() => import('./pages/dashboard/DashIssuesPage'));
const DashReferralsPage = lazy(() => import('./pages/dashboard/DashReferralsPage'));
const AdminReferralsPage = lazy(() => import('./pages/admin/AdminReferralsPage'));
const AdminSocialPage = lazy(() => import('./pages/admin/AdminSocialPage'));
const ChallengePage = lazy(() => import('./pages/ChallengePage'));
const DashFeatureRequestsPage = lazy(() => import('./pages/dashboard/DashFeatureRequestsPage'));
const AdminIssuesPage = lazy(() => import('./pages/admin/AdminIssuesPage'));
const AdminFeatureRequestsPage = lazy(() => import('./pages/admin/AdminFeatureRequestsPage'));
const BillingSubscriptionsPage = lazy(() => import('./pages/admin/BillingSubscriptionsPage'));
const BillingPaymentsPage = lazy(() => import('./pages/admin/BillingPaymentsPage'));
const BillingPlansPage = lazy(() => import('./pages/admin/BillingPlansPage'));
const BillingCouponsPage = lazy(() => import('./pages/admin/BillingCouponsPage'));
const AdminNotificationsPage = lazy(() => import('./pages/admin/AdminNotificationsPage'));

// Old `/admin?tab=…` links from the v1 tabbed admin page.
const LEGACY_ADMIN_TABS = {
  users: '/admin/users',
  data: '/admin/data/imports',
  abbreviations: '/admin/data/abbreviations',
  synonyms: '/admin/data/synonyms',
};

function AdminIndex() {
  const [params] = useSearchParams();
  const tab = params.get('tab');
  if (tab === 'dashboard') return <Navigate to="/admin" replace />;
  if (tab && LEGACY_ADMIN_TABS[tab]) return <Navigate to={LEGACY_ADMIN_TABS[tab]} replace />;
  return <OverviewPage />;
}

const VERIFY_BANNER_KEY = 'icd_verify_banner_dismissed';
const NO_BANNER_PATHS = ['/verify-email', '/login', '/register', '/auth/'];

/** Dismissible (per browser session) reminder for signed-in users who have not confirmed their email yet. */
function VerifyEmailBanner() {
  const { needsVerification } = useAuth();
  const location = useLocation();
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(VERIFY_BANNER_KEY) === '1';
    } catch {
      return false;
    }
  });
  if (!needsVerification || dismissed || NO_BANNER_PATHS.some((p) => location.pathname.startsWith(p))) return null;
  const dismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem(VERIFY_BANNER_KEY, '1');
    } catch {
      // storage blocked: dismissed for this page view only
    }
  };
  const next = encodeURIComponent(location.pathname + location.search);
  return (
    <div className="border-b border-warn/30 bg-warn-soft" role="status">
      <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 py-2.5 text-sm text-ink">
        <MailWarning className="h-4 w-4 shrink-0 text-warn" aria-hidden="true" />
        <p className="min-w-0 flex-1">
          <strong>Verify your email</strong> to switch on your free AI credits.{' '}
          <Link to={`/verify-email?next=${next}`} className="font-bold">
            Enter your code
          </Link>
        </p>
        <button type="button" onClick={dismiss} className="rounded-full p-1.5 text-muted transition hover:bg-surface-2 hover:text-ink" aria-label="Dismiss">
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

/** Public site: the shared Layout and the route fade wrap only the non-admin routes. */
function PublicApp() {
  const location = useLocation();
  // A new page starts at the top (hash links keep their own scroll target).
  useLayoutEffect(() => {
    if (!window.location.hash) window.scrollTo(0, 0);
  }, [location.pathname]);
  // Dashboard sub-pages share one shell, so only entering the shell (not every tab switch) replays the fade.
  const pageKey = location.pathname.startsWith('/dashboard') ? '/dashboard' : location.pathname;

  return (
    <Layout>
      <VerifyEmailBanner />
      {/*
        Suspense sits OUTSIDE the keyed wrapper, so a lazy page chunk finishes loading (behind the loader)
        before the wrapper mounts. The enter fade is a CSS keyframe (.page-enter), not an AnimatePresence
        exit/enter pair: a CSS animation always runs to its end state, so a page can never be left at opacity 0.
      */}
      <Suspense fallback={<PageLoader />}>
        <div key={pageKey} className="page-enter">
          <ErrorBoundary
            resetKey={location.pathname}
            homeTo={location.pathname.startsWith('/dashboard') ? '/dashboard' : '/'}
            homeLabel={location.pathname.startsWith('/dashboard') ? 'Go to dashboard' : 'Go to home'}
          >
            <Routes location={location}>
              <Route path="/" element={<HomePage />} />
              <Route path="/search" element={<SearchPage />} />
              <Route path="/icd10/:code" element={<CodeDetailPage />} />
              <Route path="/chapters" element={<ChaptersPage />} />
              <Route path="/chapters/:chapter" element={<ChapterPage />} />
              <Route path="/blocks/:block" element={<BlockPage />} />
              <Route path="/index" element={<IndexBrowserPage />} />
              <Route path="/gems" element={<GemsPage />} />
              <Route path="/updates" element={<UpdatesPage />} />
              <Route path="/favorites" element={<FavoritesPage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/ai/note-to-codes" element={<NoteToCodesPage />} />
              <Route path="/ai/query-builder" element={<QueryBuilderPage />} />
              <Route path="/ai/medical-necessity" element={<MedicalNecessityPage />} />
              <Route path="/ai/guidelines-chat" element={<GuidelinesChatPage />} />
              <Route path="/code-sets" element={<CodeSetsPage />} />
              <Route path="/code-sets/:id" element={<CodeSetDetailPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="/auth/microsoft/callback" element={<MicrosoftCallbackPage />} />
              <Route path="/account" element={<AccountPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/team" element={<TeamPage />} />
              <Route path="/team/accept" element={<AcceptInvitePage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/challenge" element={<ChallengePage />} />
              <Route path="/invite" element={<InviteRedirect />} />
              <Route path="/dashboard" element={<DashboardLayout />}>
                <Route index element={<DashOverviewPage />} />
                <Route path="codes" element={<DashCodesPage />} />
                <Route path="code-sets" element={<CodeSetsPage />} />
                <Route path="code-sets/:id" element={<CodeSetDetailPage />} />
                <Route path="ai" element={<DashAiPage />} />
                <Route path="billing" element={<DashBillingPage />} />
                <Route path="team" element={<TeamPage />} />
                <Route path="notifications" element={<DashNotificationsPage />} />
                <Route path="profile" element={<DashProfilePage />} />
                <Route path="security" element={<DashSecurityPage />} />
                <Route path="privacy" element={<DashPrivacyPage />} />
                <Route path="support" element={<DashSupportPage />} />
                <Route path="issues" element={<DashIssuesPage />} />
                <Route path="referrals" element={<DashReferralsPage />} />
                <Route path="features" element={<DashFeatureRequestsPage />} />
              </Route>
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </ErrorBoundary>
        </div>
      </Suspense>
    </Layout>
  );
}

/** /invite?ref=CODE is the shareable referral link (prerendered for social previews); the app sends people to sign-up with the code kept. */
function InviteRedirect() {
  const location = useLocation();
  return <Navigate to={`/register${location.search}`} replace />;
}

export default function App() {
  useAnalyticsPageviews();

  return (
    <MotionConfig reducedMotion="user">
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminIndex />} />
            <Route path="analytics" element={<Navigate to="/admin/analytics/traffic" replace />} />
            <Route path="analytics/traffic" element={<AnalyticsTrafficPage />} />
            <Route path="analytics/sources" element={<AnalyticsSourcesPage />} />
            <Route path="analytics/audience" element={<AnalyticsAudiencePage />} />
            <Route path="analytics/realtime" element={<RealtimePage />} />
            <Route path="funnel" element={<FunnelPage />} />
            <Route path="retention" element={<RetentionPage />} />
            <Route path="product" element={<ProductUsagePage />} />
            <Route path="revenue" element={<RevenuePage />} />
            <Route path="ai" element={<Navigate to="/admin/ai/engine" replace />} />
            <Route path="ai/engine" element={<AiEnginePage />} />
            <Route path="ai/plans" element={<AiPlansPage />} />
            <Route path="ai/usage" element={<AiUsagePage />} />
            <Route path="ai/lab" element={<AiLabPage />} />
            <Route path="crm" element={<Navigate to="/admin/crm/contacts" replace />} />
            <Route path="crm/contacts" element={<CrmContactsPage />} />
            <Route path="crm/contacts/:id" element={<CrmContactDetailPage />} />
            <Route path="crm/pipeline" element={<CrmPipelinePage />} />
            <Route path="crm/leads" element={<CrmLeadsPage />} />
            <Route path="crm/tasks" element={<CrmTasksPage />} />
            <Route path="crm/segments" element={<CrmSegmentsPage />} />
            <Route path="automations" element={<AutomationsPage />} />
            <Route path="automations/new" element={<AutomationEditorPage />} />
            <Route path="automations/runs" element={<AutomationRunsPage />} />
            <Route path="automations/:id" element={<AutomationEditorPage />} />
            <Route path="email" element={<Navigate to="/admin/email/templates" replace />} />
            <Route path="email/templates" element={<EmailTemplatesPage />} />
            <Route path="email/templates/new" element={<EmailTemplateEditorPage />} />
            <Route path="email/templates/:id" element={<EmailTemplateEditorPage />} />
            <Route path="email/campaigns" element={<CampaignsPage />} />
            <Route path="email/logs" element={<EmailLogsPage />} />
            <Route path="webhooks" element={<WebhooksPage />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="teams" element={<TeamsPage />} />
            <Route path="data" element={<Navigate to="/admin/data/imports" replace />} />
            <Route path="data/imports" element={<DataImportsPage />} />
            <Route path="data/abbreviations" element={<AbbreviationsPage />} />
            <Route path="data/synonyms" element={<SynonymsPage />} />
            <Route path="system" element={<Navigate to="/admin/system/health" replace />} />
            <Route path="system/health" element={<SystemHealthPage />} />
            <Route path="system/errors" element={<ErrorsPage />} />
            <Route path="system/audit" element={<AuditLogPage />} />
            <Route path="settings" element={<AdminSettingsPage />} />
            <Route path="settings/theme" element={<AdminThemePage />} />
            <Route path="settings/sign-in" element={<AuthProvidersPage />} />
            <Route path="blog" element={<BlogHomePage />} />
            <Route path="blog/posts" element={<BlogPostsPage />} />
            <Route path="blog/posts/new" element={<BlogEditorPage />} />
            <Route path="blog/posts/:id" element={<BlogEditorPage />} />
            <Route path="blog/topics" element={<BlogTopicsPage />} />
            <Route path="blog/autopilot" element={<BlogAutopilotPage />} />
            <Route path="blog/settings" element={<BlogSettingsPage />} />
            <Route path="inbox" element={<InboxPage />} />
            <Route path="feedback" element={<Navigate to="/admin/feedback/issues" replace />} />
            <Route path="feedback/issues" element={<AdminIssuesPage />} />
            <Route path="growth/referrals" element={<AdminReferralsPage />} />
            <Route path="growth/social" element={<AdminSocialPage />} />
            <Route path="feedback/features" element={<AdminFeatureRequestsPage />} />
            <Route path="email/newsletter" element={<NewsletterPage />} />
            <Route path="reports/weekly" element={<WeeklyReportPage />} />
            <Route path="billing" element={<Navigate to="/admin/billing/subscriptions" replace />} />
            <Route path="billing/subscriptions" element={<BillingSubscriptionsPage />} />
            <Route path="billing/payments" element={<BillingPaymentsPage />} />
            <Route path="billing/plans" element={<BillingPlansPage />} />
            <Route path="billing/coupons" element={<BillingCouponsPage />} />
            <Route path="notifications" element={<AdminNotificationsPage />} />
            <Route path="*" element={<AdminNotFound />} />
          </Route>
          <Route path="/*" element={<PublicApp />} />
        </Routes>
      </Suspense>
    </MotionConfig>
  );
}
